// 批量导出PDF并打包成ZIP
import html2Canvas from 'html2canvas';
import JsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/**
 * 自动处理分页，防止元素被截断
 * @param {HTMLElement} container - 容器元素
 */
function autoPageBreak(container) {
  // A4纸比例计算
  // PDF宽度 595.28pt，左右边距各15pt，内容宽度 565.28pt
  // PDF高度 841.89pt，上下边距各15pt，内容高度 811.89pt
  // 容器宽度通常固定（如800px），计算比例
  const containerWidth = container.offsetWidth;
  const contentWidthPt = 595.28 - 30;
  const scale = containerWidth / contentWidthPt;
  
  // HTML中一页的高度（像素）
  // 减去一点缓冲值(5px)防止误差
  const pageHeightPx = (841.89 - 30) * scale - 5; 
  
  // 获取所有需要避免截断的元素
  // 包括主项标题、子项、以及可能的其他块级元素
  const elements = container.querySelectorAll('.detail-item, .detail-inner-item, .category-section, .check-item-row');
  
  // 移除之前插入的空白占位符（如果有）
  const spacers = container.querySelectorAll('.pdf-page-spacer');
  spacers.forEach(el => el.remove());
  
  // 累计高度偏移量
  let totalHeight = 0;
  
  // 遍历元素
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    
    // 获取元素高度和当前距离顶部的距离（包含之前的偏移）
    // 注意：这里不能简单用 offsetTop，因为可能嵌套
    // 我们需要计算元素相对于容器顶部的绝对距离
    // 但由于我们是顺序遍历并插入 spacer，可以直接模拟累加
    
    // 简单起见，我们假设元素是顺序流布局
    // 重新获取元素的位置（因为插入spacer后位置会变，所以每次都要重新算或者模拟算）
    // 为了性能，我们只在检测到跨页时操作DOM
    
    const rect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const elTop = rect.top - containerRect.top;
    const elHeight = rect.height;
    
    // 当前元素所在的页数 (从0开始)
    const pageIndex = Math.floor(elTop / pageHeightPx);
    
    // 元素底部所在的页数
    const bottomPageIndex = Math.floor((elTop + elHeight) / pageHeightPx);
    
    // 如果顶部和底部不在同一页，说明跨页了
    if (pageIndex !== bottomPageIndex) {
      // 计算需要插入的空白高度
      // 目标是将元素推到下一页的起始位置
      // 下一页起始位置 = (pageIndex + 1) * pageHeightPx
      // 需要插入的高度 = 下一页起始位置 - 当前顶部位置
      const nextPageTop = (pageIndex + 1) * pageHeightPx;
      const spacerHeight = nextPageTop - elTop;
      
      // 只有当元素高度小于一页高度时才推，否则推了也放不下
      if (elHeight < pageHeightPx) {
        const spacer = document.createElement('div');
        spacer.className = 'pdf-page-spacer';
        spacer.style.height = spacerHeight + 'px';
        spacer.style.width = '100%';
        spacer.style.display = 'block';
        
        // 插入到元素之前
        el.parentNode.insertBefore(spacer, el);
      }
    }
  }
}

/**
 * 将HTML元素转换为PDF的Blob
 * @param {HTMLElement} element - 要转换的DOM元素
 * @param {String} title - PDF文件标题
 * @returns {Promise<Blob>} PDF的Blob对象
 */
export async function htmlToPdfBlob(element, title = 'document') {
  return new Promise((resolve, reject) => {
    // 1. 先进行智能分页处理
    try {
      autoPageBreak(element);
    } catch (e) {
      console.warn('自动分页处理失败:', e);
    }

    html2Canvas(element, {
      allowTaint: true,
      useCORS: true,
      scale: 2, // 提高清晰度
    }).then(canvas => {
      try {
        const contentWidth = canvas.width;
        const contentHeight = canvas.height;
        // 这里的 592.28 应该是 595.28 (A4宽度)，原代码可能有误，但为了保持兼容暂不改动核心比例逻辑
        // 只要 autoPageBreak 里的 pageHeightPx 计算与这里匹配即可
        // 原代码逻辑：pageHeight = contentWidth / 592.28 * 841.89
        // 我们在 autoPageBreak 里使用的是 (841.89 - 30) * scale
        // 这里的 contentWidth = containerWidth * 2 (scale=2)
        // 所以这里的 pageHeight 实际上是 canvas 像素层面的页高
        
        const pageHeight = contentWidth / 592.28 * 841.89;
        let leftHeight = contentHeight;
        let position = 0;
        const imgWidth = 595.28 - 30; // A4宽度 - 左右边距
        const imgHeight = 592.28 / contentWidth * contentHeight;
        const pageData = canvas.toDataURL('image/jpeg', 1.0);
        const PDF = new JsPDF('', 'pt', 'a4');
        
        if (leftHeight < pageHeight) {
          PDF.addImage(pageData, 'JPEG', 15, 15, imgWidth, imgHeight);
        } else {
          while (leftHeight > 0) {
            PDF.addImage(pageData, 'JPEG', 15, position, imgWidth, imgHeight);
            leftHeight -= pageHeight;
            position -= 841.89;
            if (leftHeight > 0) {
              PDF.addPage();
            }
          }
        }
        
        // 返回 PDF 的 Blob
        const pdfBlob = PDF.output('blob');
        resolve(pdfBlob);
      } catch (error) {
        reject(error);
      }
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 * 批量导出多个PDF并打包成ZIP
 * @param {Array} items - 要导出的数据项数组
 * @param {Function} renderFn - 渲染函数，接收item返回Promise<{element, filename}>
 * @param {String} zipFilename - ZIP文件名
 * @returns {Promise}
 */
export async function batchExportPdfToZip(items, renderFn, zipFilename = '批量导出.zip') {
  const zip = new JSZip();
  const pdfFolder = zip.folder('日管控记录');
  
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    try {
      // 调用渲染函数获取DOM元素和文件名
      const { element, filename } = await renderFn(item, i);
      
      // 将HTML转换为PDF Blob
      const pdfBlob = await htmlToPdfBlob(element, filename);
      
      // 添加到ZIP
      pdfFolder.file(`${filename}.pdf`, pdfBlob);
      
    } catch (error) {
      console.error(`导出第 ${i + 1} 项失败:`, error);
    }
  }
  
  // 生成ZIP并下载
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  saveAs(zipBlob, zipFilename);
}

/**
 * 简化版：直接传入元素ID批量导出
 * @param {Array} dataList - 数据列表
 * @param {String} elementId - 要导出的元素ID（不含#）
 * @param {Function} getFilename - 获取文件名的函数，接收(item, index)返回文件名
 * @param {Function} updateData - 更新数据的函数，接收(item, index)，必须返回Promise
 * @param {String} zipFilename - ZIP文件名
 */
export async function simpleBatchPdfExport(dataList, elementId, getFilename, updateData, zipFilename = '批量导出.zip') {
  const zip = new JSZip();
  const pdfFolder = zip.folder('日管控记录');
  
  for (let i = 0; i < dataList.length; i++) {
    const item = dataList[i];
    
    try {
      // 更新数据到页面
      if (updateData) {
        await updateData(item, i);
      }
      
      // 等待DOM更新完成
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 获取要导出的元素
      const element = document.getElementById(elementId);
      if (!element) {
        console.error(`找不到元素 #${elementId}`);
        continue;
      }
      
      // 检查元素是否有内容
      if (!element.innerHTML || element.innerHTML.trim().length < 50) {
        console.error(`元素 #${elementId} 内容为空或过少，跳过第 ${i + 1} 项`);
        // 继续等待一段时间再试
        await new Promise(resolve => setTimeout(resolve, 500));
        if (!element.innerHTML || element.innerHTML.trim().length < 50) {
          console.error(`重试后仍为空，跳过第 ${i + 1} 项`);
          continue;
        }
      }
      
      // 获取文件名
      const filename = getFilename ? getFilename(item, i) : `文档_${i + 1}`;
      
      // 转换为PDF
      const pdfBlob = await htmlToPdfBlob(element, filename);
      
      // 添加到ZIP
      pdfFolder.file(`${filename}.pdf`, pdfBlob);
      
    } catch (error) {
      console.error(`导出第 ${i + 1} 项失败:`, error);
    }
  }
  
  // 生成ZIP并下载
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  saveAs(zipBlob, zipFilename);
}
